import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { recordLogin } from '../../lib/recordLogin';
import { useAuth } from '../../contexts/AuthContext';
import { useLoginFSM } from '../../hooks/useLoginFSM';
import { AlertCircle, Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import TwoFactorModal from '../../components/TwoFactorModal';
import ForgotPasswordFlow from '../../components/ForgotPasswordFlow';
import Navbar from '../../components/Navbar';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, loading, signOut } = useAuth();
  const from = (location.state as any)?.from?.pathname || '/admin/dashboard';

  const { auth, needs2FA, login, checkAndTransition, on2FASuccess, on2FACancel } = useLoginFSM({
    userType: 'admin',
    onSignOut: signOut,
  });

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);

  // ── ONLY navigation rule ──────────────────────────────────────────────────
  useEffect(() => {
    if (auth.state === 'authenticated' && auth.user && isAdmin) {
      navigate(from, { replace: true });
    }
  }, [auth.state, auth.user, isAdmin]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ── Login handler — single-flight, only triggers FSM transitions, never navigates ──
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      const { success, user, session } = await login(formData.email, formData.password);

      if (!success || !user) {
        recordLogin({
          user_id: '',
          user_type: 'admin',
          email: formData.email,
          success: false,
          failure_reason: auth.error || 'Unknown error',
        });
        return;
      }

      recordLogin({
        user_id: user.id,
        user_type: 'admin',
        email: formData.email,
        success: true,
        session_id: session?.access_token?.slice(-16) || '',
      });

      await checkAndTransition(user, session?.access_token || '');
    } finally {
      setSubmitting(false);
    }
  };

  const isBusy = submitting || auth.state === 'authenticating' || auth.state === 'verifying_2fa';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />

      {/* 2FA modal — mounts ONLY when FSM state === 'needs_2fa' */}
      {needs2FA && auth.accessToken && (
        <TwoFactorModal
          accessToken={auth.accessToken}
          onSuccess={() => on2FASuccess(auth.user!)}
          onCancel={on2FACancel}
        />
      )}

      <div className="flex-1 flex flex-col items-center justify-center px-4 pt-32 pb-12">
        <div className="max-w-md w-full space-y-8">

          {auth.user && !isAdmin && (
            <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
              <div className="flex items-center gap-2 text-yellow-700">
                <AlertCircle className="w-5 h-5" />
                <p className="text-sm font-semibold">Access Denied</p>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                You don't have admin privileges. Please contact support.
              </p>
            </div>
          )}

          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
            {forgotPasswordMode ? (
              <ForgotPasswordFlow
                onBack={() => setForgotPasswordMode(false)}
                accentColor="#40E0D0"
                userType="admin"
              />
            ) : (
              <form onSubmit={handleLogin} className="space-y-6">
                {auth.state === 'error' && auth.error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-600">{auth.error}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent text-gray-900 placeholder-gray-400"
                      placeholder="admin@example.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full pl-10 pr-12 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent text-gray-900 placeholder-gray-400"
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => setForgotPasswordMode(true)}
                    className="text-sm text-[#40E0D0] hover:underline"
                  >
                    Forgot Password?
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={isBusy}
                  className="w-full bg-[#40E0D0] text-white font-bold py-3 rounded-lg hover:bg-[#35c5b5] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isBusy
                    ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Signing in...</>
                    : 'Sign In'
                  }
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
